import { useCallback, useState } from 'react'
import { UI_TREE, uiNode, uiTabValues } from '@rs/policy/ui-tree'
import { can } from '@rs/policy/grants'
import { useRoleOptional } from '../app/auth.tsx'

/**
 * ══ TAB CỦA MÀN — MỘT CHỖ CHO ĐỌC ĐƯỜNG DẪN · TÊN CŨ · TAB MẶC ĐỊNH · TAB BỊ ẨN (D03c §7.2) ══════════════
 *
 * Không vẽ gì: mỗi màn giữ nguyên kiểu thanh tab của nó (bản cũ mỗi màn một kiểu — §7 bám giao diện). Hook chỉ gom
 * phần logic mà trước đây mỗi màn tự viết một kiểu, và buộc mọi tab phải có mặt trong sổ đăng ký `@rs/policy/ui-tree`
 * — Đợt 1 phân quyền theo đúng sổ đó, tab không khai là tab không ai cấp quyền được.
 *
 * Đọc đường dẫn: chỉ khi nút cha khai `param` trong sổ (giữ đúng hành vi hiện có của từng màn — màn nào trước đây bỏ
 * qua `?tab=` thì vẫn bỏ qua). Chỉ đọc MỘT lần lúc vào màn, như các màn đang làm.
 */
export interface ScreenTabOptions<T extends string> {
  /** Tab mở khi đường dẫn không chỉ tab nào, chỉ tab lạ, hoặc tab bị ẩn. Hàm ⇒ tính một lần lúc vào màn. */
  fallback: T | (() => T)
  /** Tên cũ trong đường dẫn ⇒ tab hiện nay (`congno` ⇒ `invoices` — link và bookmark cũ vẫn đi đúng chỗ). */
  aliases?: Readonly<Record<string, T>>
  /** Tab có hiện với người đang xem không. Vắng ⇒ mọi tab. Đợt 1 thay bằng `can(…, 'view')`. */
  visible?: (tab: T) => boolean
}

/** Phần thuần của hook — chọn tab lúc vào màn. Tách ra để test không cần React. */
export const pickInitialTab = <T extends string>(
  values: readonly string[], want: string | null, opts: ScreenTabOptions<T>,
): T => {
  const ok = (t: string): t is T => values.includes(t) && (opts.visible?.(t as T) ?? true)
  const w = want === null ? null : (opts.aliases?.[want] ?? want)
  if (w !== null && ok(w)) return w
  const fb = typeof opts.fallback === 'function' ? opts.fallback() : opts.fallback
  if (ok(fb)) return fb
  const first = values.find(ok)
  return (first ?? fb) as T
}

/** Cùng cách `@rs/ui/i18n` (`isDevOrTest`): dev và test thì tab chưa khai là LỖI, production thì im. */
const isDev = (): boolean => (import.meta as ImportMeta & { env?: { DEV?: boolean } }).env?.DEV === true

const assertKnown = (nodeId: string, values: readonly string[], t: string): void => {
  if (isDev() && !values.includes(t)) {
    throw new Error(`Tab "${t}" của "${nodeId}" chưa khai trong @rs/policy/ui-tree — thêm nút trước khi vẽ tab.`)
  }
}

/** Nút sổ ứng với giá trị tab `t` ngay dưới `nodeId` (đi xuyên nút nhóm như `uiTabValues`). */
const nodeOfTab = (nodeId: string, t: string): string | undefined => {
  for (const n of UI_TREE) {
    if (n.kind !== 'tab' || n.tab !== t) continue
    const p = uiNode(n.parent ?? '')
    if (n.parent === nodeId || (p?.group === true && p.parent === nodeId)) return n.id
  }
  return undefined
}

/**
 * `[tab, setTab]` cho cấp tab ngay dưới nút `nodeId`. Tab đang chọn mà trở thành ẩn (vai nạp xong muộn, quyền đổi)
 * thì trả tab hiện được đầu tiên — màn không bao giờ vẽ thân của một tab người xem không được thấy.
 *
 * ⭐ D03c Đợt 1c: tab vai không được XEM (`can(vai, nút, 'view')` — màn Danh mục ▸ 🔐 Phân quyền) tự ẩn, cộng với điều
 * kiện `visible` riêng của màn (cờ giá vốn của người…). Ngoài `<AuthProvider>` (ca kiểm) thì chỉ xét `visible`.
 * ⚠ Màn tự vẽ thanh tab ⇒ vẫn phải gọi `isShown` khi vẽ nút tab — hook không vẽ gì.
 */
export const useScreenTab = <T extends string>(nodeId: string, opts: ScreenTabOptions<T>): [T, (t: T) => void, (t: T) => boolean] => {
  const values = uiTabValues(nodeId)
  const role = useRoleOptional()
  const ownVisible = opts.visible
  const isShown = (t: T): boolean => {
    if (ownVisible !== undefined && !ownVisible(t)) return false
    if (role === null) return true
    const node = nodeOfTab(nodeId, t)
    return node === undefined || can(role, node, 'view')
  }
  const eff: ScreenTabOptions<T> = { ...opts, visible: isShown }
  const [tab, setTab] = useState<T>(() => {
    const param = uiNode(nodeId)?.param
    const want = param === undefined ? null : new URLSearchParams(window.location.search).get(param)
    const t = pickInitialTab(values, want, eff)
    assertKnown(nodeId, values, t)
    return t
  })
  const shown = isShown(tab) ? tab : pickInitialTab(values, null, eff)
  /* Ổn định như setter của `useState` — màn hay gọi nó trong `useEffect` (vd `ap.tsx` đổi tab theo `?stmt=`). */
  const set = useCallback((t: T): void => {
    assertKnown(nodeId, uiTabValues(nodeId), t)
    setTab(t)
  }, [nodeId])
  return [shown, set, isShown]
}
